import React from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { useI18n, SUPPORTED_LANGUAGES, ROUTE_MAPPINGS } from '../context/I18nContext';
import { Globe, ChevronDown } from 'lucide-react';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from './ui/dropdown-menu';
import { Button } from './ui/button';

const LanguageSwitcher = () => {
  const { locale } = useI18n();
  const navigate = useNavigate();
  const location = useLocation();
  
  const currentLang = SUPPORTED_LANGUAGES.find(l => l.code === locale);

  // Get current route key from path
  const getCurrentRouteKey = () => {
    const segments = location.pathname.split('/').filter(Boolean);
    const supportedLangs = SUPPORTED_LANGUAGES.map(l => l.code);
    
    // If first segment is a language code, get second segment
    let routeSegment = segments[0];
    if (supportedLangs.includes(segments[0])) {
      routeSegment = segments[1];
    }
    
    if (!routeSegment) return 'home';
    
    // Find route key in current locale mappings
    const mappings = ROUTE_MAPPINGS[locale] || ROUTE_MAPPINGS.it;
    for (const [key, value] of Object.entries(mappings)) {
      if (value === routeSegment) return key;
    }
    
    // Also check Italian mappings as fallback
    for (const [key, value] of Object.entries(ROUTE_MAPPINGS.it)) {
      if (value === routeSegment) return key;
    }
    
    return 'home';
  };

  const handleLanguageChange = (newLang) => {
    if (newLang === locale) return;
    
    const routeKey = getCurrentRouteKey();
    const newRoute = ROUTE_MAPPINGS[newLang]?.[routeKey] || '';
    
    // Build new path
    let newPath;
    if (newLang === 'it') {
      newPath = newRoute ? `/${newRoute}` : '/';
    } else {
      newPath = newRoute ? `/${newLang}/${newRoute}` : `/${newLang}`;
    }
    
    // Preserve query string (for health card data)
    const queryString = location.search;
    
    // Save new locale
    localStorage.setItem('cf_locale', newLang);
    
    // Navigate to new URL
    window.location.href = newPath + queryString;
  };

  return (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        <Button variant="ghost" size="sm" className="gap-2" data-testid="language-switcher">
          <Globe className="h-4 w-4" />
          <span className="hidden sm:inline">{currentLang?.flag} {currentLang?.name}</span>
          <span className="sm:hidden">{currentLang?.flag}</span>
          <ChevronDown className="h-3 w-3" />
        </Button>
      </DropdownMenuTrigger>
      <DropdownMenuContent align="end" className="w-48">
        {SUPPORTED_LANGUAGES.map((lang) => (
          <DropdownMenuItem
            key={lang.code}
            onClick={() => handleLanguageChange(lang.code)}
            className={`cursor-pointer ${locale === lang.code ? 'bg-emerald-50 text-emerald-700' : ''}`}
            data-testid={`lang-${lang.code}`}
          >
            <span className="mr-2">{lang.flag}</span>
            {lang.name}
          </DropdownMenuItem>
        ))}
      </DropdownMenuContent>
    </DropdownMenu>
  );
};

export default LanguageSwitcher;
